import React from 'react';
import { theme as antdTheme } from 'antd';
import type { VcsApi } from '@env0/vcs-service/api';
import { css } from '@emotion/react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from '@emotion/styled';
import Select, { SelectOption } from 'components/common/input-components/select';
import withLabel from 'components/common/form-controlled/with-label.hoc';
import { FieldRow } from 'components/common/form-components';
import Link from 'components/common/link';
import Tooltip from 'components/common/tooltip';
import type { VCSForm } from 'components/templates/templates-wizard/common/template-wizard.types';
import { useCurrentOrganizationId } from 'hooks/use-current-organization-id';
import { useHasPermission } from 'hooks/use-has-permission';
import { useCachedVcsRepository, useGetVcsConnections } from 'stores/rq/vcs-connections';
import { useVcsRepositoriesFirstPage, useVcsRepositoryLookup } from './use-vcs-repositories-search.hook';

type Props = {
  form: VCSForm;
  vcsType: VcsApi.VcsType;
};

// Driven off the form directly rather than through `ControlledSelect`: react-hook-form v6's `Controller` keeps
// the value it mounted with, and the repository dropdown re-pins the connection while this field is on screen.
const LabeledSelect = withLabel(Select);

// Connections are often unnamed, and the settings screen identifies them by scope, so the scope is what the
// user has to recognise here. The id is the last resort for a connection they cannot list.
const connectionLabel = ({ id, name, accessScope }: VcsApi.RepositoryVcsConnection) => {
  // A token-based connection's scope is its token name, which is often the connection name too.
  if (name && accessScope && name !== accessScope) return `${name} (${accessScope})`;

  return name || accessScope || id;
};

const VcsConnectionSelectField: React.FunctionComponent<Props> = ({ form, vcsType }) => {
  // A named watch: `watch()` only reports registered fields once any field registers, and the repository input
  // may live in another component (or nowhere, in edit mode) while this one is mounted.
  const repository = form.watch('repository');
  const [vcsConnectionId, setVcsConnectionId] = form.useManualField<string | null>('vcsConnectionId');
  const intl = useIntl();
  const organizationId = useCurrentOrganizationId();
  const firstPage = useVcsRepositoriesFirstPage(vcsType, 'DeploymentPipeline');
  // The first page, or the search result the repository was just picked from, usually holds it already. Only a
  // repository pinned on an existing template and outside the first page needs its own request.
  const cachedRepository = useCachedVcsRepository(vcsType, 'DeploymentPipeline', repository);
  const lookup = useVcsRepositoryLookup(
    vcsType,
    'DeploymentPipeline',
    repository,
    !firstPage.isPending && !cachedRepository
  );
  // Only users who can view connections may list them; everyone else sees the id when the name is unknown.
  const { data: organizationConnections } = useGetVcsConnections();
  const { isAuthorized: canManageVcsConnections } = useHasPermission('MANAGE_VCS_CONNECTIONS');
  const { token } = antdTheme.useToken();

  const selectedRepository =
    cachedRepository ?? lookup.data?.repositories.find(({ httpsUrl }) => httpsUrl === repository);
  const reachableConnections: VcsApi.RepositoryVcsConnection[] = selectedRepository
    ? (selectedRepository.vcsConnections ?? [
        { id: selectedRepository.vcsConnectionId, name: selectedRepository.connectionName }
      ])
    : [];

  // A pinned connection that no longer reaches the repository (or a repository neither query returns) is still
  // shown, so the user can see what the template is pinned to and move it to a connection that does reach.
  const isPinnedListed = reachableConnections.some(({ id }) => id === vcsConnectionId);
  const pinnedConnection = organizationConnections?.find(({ id }) => id === vcsConnectionId);
  const connections: Array<VcsApi.RepositoryVcsConnection & { isUnreachable?: boolean }> =
    vcsConnectionId && !isPinnedListed
      ? [
          ...reachableConnections,
          {
            id: vcsConnectionId,
            name: pinnedConnection?.name ?? '',
            accessScope: pinnedConnection?.accessScope,
            isUnreachable: true
          }
        ]
      : reachableConnections;

  const isFetchingConnections = firstPage.isFetching || lookup.isFetching;
  const placeholderId = !repository
    ? 'templates.add.new.field.vcs-connection.placeholder'
    : connections.length === 0 && !isFetchingConnections
      ? 'templates.add.new.field.vcs-connection.placeholder.none'
      : undefined;

  return (
    <ConnectionFieldRow>
      <LabeledSelect
        inline
        width={50}
        label={{ id: 'templates.add.new.field.vcs-connection' }}
        info={{ id: 'templates.add.new.field.vcs-connection.info' }}
        data-e2e="vcs-connection-selector"
        value={vcsConnectionId ?? undefined}
        onChange={setVcsConnectionId}
        // Only while there is nothing to show: `Select` swaps its options for a spinner when loading, and a
        // selected connection whose option is missing renders as its raw id.
        loading={connections.length === 0 && isFetchingConnections}
        placeholder={placeholderId && intl.formatMessage({ id: placeholderId })}
        disabled={form.readonly || connections.length < 2}>
        {connections.map(connection => (
          <SelectOption
            key={connection.id}
            value={connection.id}
            disabled={connection.isUnreachable}
            data-e2e={`vcs-connection-option-${connection.id}`}>
            {connectionLabel(connection)}
          </SelectOption>
        ))}
      </LabeledSelect>
      <ManageConnectionsSlot controlHeight={token.controlHeight}>
        {canManageVcsConnections ? (
          <Link
            url={`/organizations/${organizationId}/vcs`}
            openInNewWindow
            messageId="templates.add.new.field.vcs-connection.manage"
            data-e2e="vcs-connection-manage-link"
          />
        ) : (
          <Tooltip titleId="permissions.missing.manage-vcs-connections" placement="top">
            <ManageConnectionsDisabled data-e2e="vcs-connection-manage-link-disabled">
              <FormattedMessage id="templates.add.new.field.vcs-connection.manage" />
            </ManageConnectionsDisabled>
          </Tooltip>
        )}
      </ManageConnectionsSlot>
    </ConnectionFieldRow>
  );
};

const ConnectionFieldRow = styled(FieldRow)`
  align-items: flex-end;

  ${({ theme }) => css`
    margin-block-start: ${theme.spacing.md};
  `}
`;

// Centred on the select beside it: the row aligns to the bottom, and the slot is the select's height. The height
// comes from antd, which renders the select, rather than being repeated here.
const ManageConnectionsSlot = styled.span<{ controlHeight: number }>`
  display: inline-flex;
  align-items: center;
  white-space: nowrap;

  ${props => css`
    margin-inline-start: ${props.theme.spacing.md};
    height: ${props.controlHeight}px;
  `}
`;

const ManageConnectionsDisabled = styled.span`
  cursor: not-allowed;

  color: var(--text-disabled);
`;

export default VcsConnectionSelectField;
